import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import { isNullOrUndefined } from '@sla-ng/core';
import { Moment } from 'moment';
import { Observable } from 'rxjs';
import { ICalculationResult } from '../models/calculation-result.model';
import { IAddCostAccounting } from '../models/cost-accounting-add.model';
import { ICostAccounting } from '../models/cost-accounting.model';
import { ICostCenterCostDistribution } from '../models/cost-center-cost-distribution.model';
import { ICostCenterCostRedistribution } from '../models/cost-center-cost-redistribution.model';
import { ICostTypeCost } from '../models/cost-type-cost.model';
import { ICostUnitContributionMargin } from '../models/cost-unit-contribution-margin';
import { ICostUnitCost } from '../models/cost-unit-cost.model';
import { ICostUnitCostingLevelCost } from '../models/cost-unit-costing-level-cost.model';
import { IFallbackOptions } from '../models/fallback-options.model';
import { IIndirectCostImport } from '../models/indirect-cost-import.model';
import { CostAccountingRequestService } from '../services/cost-accounting-request.request.service';

const BASE_URL: string = 'cost-accounting';
const DATEFORMAT: string = 'YYYY-MM-DD';

@Injectable({
    providedIn: 'root',
})
export class CostAccountingResource {
    constructor(private myCostAccountingRequestService: CostAccountingRequestService) {
    }

    public getCostAccountingList(costAccountingType?: number, startDate?: Moment, endDate?: Moment): Observable<ICostAccounting[]> {
        const params: Params = {};
        if (costAccountingType) {
            params[ 'costAccountingType' ] = costAccountingType.toString();
        }
        if (startDate && endDate) {
            params[ 'startDate' ] = startDate.format(DATEFORMAT);
            params[ 'endDate' ] = endDate.format(DATEFORMAT);
        }
        return this.myCostAccountingRequestService.get<ICostAccounting[]>(BASE_URL, { params });
    }

    public initializeCostAccounting(costAccounting: IAddCostAccounting): Observable<ICostAccounting> {
        return this.myCostAccountingRequestService.post(BASE_URL, costAccounting);
    }

    public continueCostAccounting(costAccountingId: number): Observable<ICalculationResult> {
        return this.myCostAccountingRequestService.post(`${BASE_URL}/${costAccountingId}/continue`, {});
    }

    public calculateOperationalAccountingSheet(costAccountingId: number): Observable<void> {
        return this.myCostAccountingRequestService.post(`${BASE_URL}/${costAccountingId}/operational-accounting-sheet`, {});
    }

    public getCostCenterCosts(costAccountingId: number): Observable<ICostCenterCostDistribution[]> {
        return this.myCostAccountingRequestService.get<ICostCenterCostDistribution[]>(`${BASE_URL}/${costAccountingId}/cost-center-cost`);
    }

    public importCostCenterCosts(costAccountingId: number, costCenterCost: IIndirectCostImport[]): Observable<ICostAccounting> {
        return this.myCostAccountingRequestService.post(`${BASE_URL}/${costAccountingId}/cost-center-cost`, costCenterCost);
    }

    public getCostTypeCosts(costAccountingId: number): Observable<ICostTypeCost[]> {
        return this.myCostAccountingRequestService.get<ICostTypeCost[]>(`${BASE_URL}/${costAccountingId}/cost-type-cost`);
    }

    public setFallbackOptions(costAccountingId: number, fallbackOptions: IFallbackOptions): Observable<ICostAccounting> {
        return this.myCostAccountingRequestService.patch(`${BASE_URL}/${costAccountingId}/fallback`, fallbackOptions);
    }

    public redistributeCostCenterCost(
        costAccountingId: number,
        costCenterCostId: number,
        redistribution: ICostCenterCostRedistribution,
    ): Observable<void> {
        return this.myCostAccountingRequestService.post(`${BASE_URL}/${costAccountingId}/cost-center-cost/${costCenterCostId}/outflow`, redistribution);
    }

    public getCostUnitCostingLevelCost(costAccountingId: number, costUnitId: number): Observable<ICostUnitCostingLevelCost[]> {
        return this.myCostAccountingRequestService.get<ICostUnitCostingLevelCost[]>(`${BASE_URL}/${costAccountingId}/cost-unit-cost/cost-unit/${costUnitId}`);
    }

    public getCostUnitCostList(costAccountingId: number, costingLevelId?: number, limit?: number, measureUnitId?: number): Observable<ICostUnitCost[]> {
        const params: Params = {};
        if (!isNullOrUndefined(costingLevelId)) {
            params[ 'costingLevel' ] = costingLevelId;
        }
        if (!isNullOrUndefined(limit)) {
            params[ 'limit' ] = limit;
        }
        if (!isNullOrUndefined(measureUnitId)) {
            params[ 'measureUnit' ] = measureUnitId;
        }
        return this.myCostAccountingRequestService.get(`${BASE_URL}/${costAccountingId}/cost-unit-cost`, { params });
    }

    public getCostUnitContributionMargins(costAccountingId: number): Observable<ICostUnitContributionMargin[]> {
        return this.myCostAccountingRequestService.get(`${BASE_URL}/${costAccountingId}/break-even-comparison`);
    }
}
